import { useState, useCallback } from 'react';

export interface UseFieldEditorReturn {
  editingField: string | null;
  editValue: string;
  startEditing: (fieldId: string, currentValue: string) => void;
  updateValue: (newValue: string) => void;
  saveField: (onSave?: (fieldId: string, newValue: string) => void) => void;
  cancelEditing: () => void;
  isEditing: (fieldId: string) => boolean;
  handleKeyDown: (e: React.KeyboardEvent, onSave?: (fieldId: string, newValue: string) => void) => void;
}

/**
 * Custom hook for managing field editing state
 * Handles inline editing functionality for form fields
 */
export const useFieldEditor = (): UseFieldEditorReturn => {
  const [editingField, setEditingField] = useState<string | null>(null);
  const [editValue, setEditValue] = useState('');
  const [initialValue, setInitialValue] = useState('');

  /**
   * Starts editing a field with the current value
   */
  const startEditing = useCallback((fieldId: string, currentValue: string) => {
    setEditingField(fieldId);
    setEditValue(currentValue);
    setInitialValue(currentValue);
  }, []);

  const updateValue = useCallback((newValue: string) => {
    setEditValue(newValue);
  }, []);

  /**
   * Saves the current field edit
   */
  const saveField = useCallback((onSave?: (fieldId: string, newValue: string) => void) => {
    if (!editingField) {
      return;
    }

    const hasChanged = editValue !== initialValue;

    if (hasChanged && onSave) {
      onSave(editingField, editValue);
    }

    setEditingField(null);
    setEditValue('');
    setInitialValue('');
  }, [editingField, editValue, initialValue]);

  /**
   * Cancels the current field edit
   */
  const cancelEditing = useCallback(() => {
    setEditingField(null);
    setEditValue('');
    setInitialValue('');
  }, []);

  /**
   * Checks if a specific field is currently being edited
   */
  const isEditing = useCallback((fieldId: string): boolean => {
    return editingField === fieldId;
  }, [editingField]);

  /**
   * Handles keyboard shortcuts for editing
   */
  const handleKeyDown = useCallback((e: React.KeyboardEvent, onSave?: (fieldId: string, newValue: string) => void) => {
    if (e.key === 'Enter') {
      saveField(onSave);
    } else if (e.key === 'Escape') {
      cancelEditing();
    }
  }, [saveField, cancelEditing]);

  return {
    editingField,
    editValue,
    startEditing,
    updateValue,
    saveField,
    cancelEditing,
    isEditing,
    handleKeyDown
  };
};
